// src/pages/admin/StylistApprovalPage.tsx
import React, { useState, useEffect } from 'react';
import { useUI } from '../../context/UIContext';
import { useAppSelector } from '../../hooks/useRedux';
import { apiClient } from '../../api/apiClient';
import '../../styles/StylistApprovalPage.css';

interface StylistResult {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  emailVerified: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvedAt?: string;
  createdAt: string;
}

const StylistApprovalPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const adminRole = useAppSelector((state) => state.auth.user?.adminRole);
  const canMutateApproval = adminRole === 'super_admin';
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved'>('all');
  const [results, setResults] = useState<StylistResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: 'approve' | 'revoke';
    userId: string;
    name: string;
  } | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  const fetchStylists = async (query: string, status: 'all' | 'pending' | 'approved') => {
    setIsLoading(true);

    try {
      const response = await apiClient.get('/v1/admin/stylist-approval/search', {
        params: {
          query,
          ...(status !== 'all' && { approvalStatus: status }),
        },
      });
      const data = response.data;

      if (data.success) {
        setResults(data.data || []);
      }
    } catch (error: any) {
      console.error('Fetch stylists error:', error);
      if (error?.status === 401 || error?.status === 403) {
        showSnackbar('You are not authorized to perform this action. Please log in again.', 'error');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else if (error?.status === 400) {
        showSnackbar(error?.message || 'Invalid request. Please check your input.', 'error');
      } else if (error?.status === 0) {
        showSnackbar('Network error. Please check your connection and try again.', 'error');
      } else {
        showSnackbar(error?.message || 'Failed to load stylists. Please try again.', 'error');
      }
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStylists(searchQuery, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleSearch = () => {
    fetchStylists(searchQuery, statusFilter);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const openConfirmModal = (action: 'approve' | 'revoke', userId: string, name: string) => {
    setConfirmModal({ open: true, action, userId, name });
  };

  const closeConfirmModal = () => {
    if (!isActioning) {
      setConfirmModal(null);
    }
  };

  const handleAction = async () => {
    if (!confirmModal) return;

    const { action, userId, name } = confirmModal;
    setIsActioning(true);

    try {
      const endpoint = action === 'approve'
        ? '/v1/admin/stylist-approval/approve'
        : '/v1/admin/stylist-approval/revoke';

      const response = await apiClient.post(endpoint, { userId });
      const data = response.data;

      if (data.success) {
        const actionLabel = action === 'approve' ? 'approved' : 'revoked';
        showSnackbar(`Successfully ${actionLabel} ${name}`, 'success');
        closeConfirmModal();
        fetchStylists(searchQuery, statusFilter);
      }
    } catch (error: any) {
      console.error('Action error:', error);
      if (error?.status === 401 || error?.status === 403) {
        showSnackbar('You are not authorized to perform this action. Please log in again.', 'error');
        closeConfirmModal();
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else if (error?.status === 404) {
        showSnackbar(error?.message || 'Stylist not found. They may have been deleted.', 'error');
        fetchStylists(searchQuery, statusFilter);
      } else if (error?.status === 400) {
        showSnackbar(error?.message || 'Invalid request. Please try again.', 'error');
      } else if (error?.status === 0) {
        showSnackbar('Network error. Please check your connection and try again.', 'error');
      } else {
        showSnackbar(error?.message || `${action === 'approve' ? 'Approval' : 'Revocation'} failed. Please try again.`, 'error');
      }
    } finally {
      setIsActioning(false);
    }
  };

  const getApprovalStatusBadge = (status: string) => {
    const map: { [key: string]: { className: string; label: string } } = {
      pending:  { className: 'badge badge-approval-pending',  label: 'Pending' },
      approved: { className: 'badge badge-approval-approved', label: 'Approved' },
      rejected: { className: 'badge badge-approval-rejected', label: 'Rejected' },
    };
    return map[status] || { className: 'badge badge-approval-default', label: status };
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  return (
    <div className="stylist-approval-page">
      <div className="stylist-approval-page__header">
        <h1>Stylist Approval</h1>
        <p>Review and manage stylist account approval for client visibility</p>
      </div>

      <div className="stylist-approval-page__search">
        <div className="search-controls">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              className="search-input"
            />
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="btn-search"
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i> Search
                </>
              )}
            </button>
          </div>
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'approved')}
              className="filter-select"
            >
              <option value="all">All Stylists</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        </div>
      </div>

      <div className="stylist-approval-page__results">
        {isLoading ? (
          <div className="empty-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading stylists...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-users"></i>
            <p>No stylists found</p>
            <small>Try adjusting your search or filter criteria</small>
          </div>
        ) : (
          <>
            <div className="results-header">
              <h3>Results ({results.length})</h3>
            </div>
            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Email Verified</th>
                    <th>Approval Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((stylist) => {
                    const approvalBadge = getApprovalStatusBadge(stylist.approvalStatus);
                    return (
                      <tr key={stylist._id}>
                        <td><strong>{stylist.name}</strong></td>
                        <td>{stylist.email}</td>
                        <td>{stylist.phoneNumber || '—'}</td>
                        <td>
                          {stylist.emailVerified ? (
                            <span className="verification-badge verified">
                              <i className="fas fa-check-circle"></i> Verified
                            </span>
                          ) : (
                            <span className="verification-badge unverified">
                              <i className="fas fa-times-circle"></i> Unverified
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={approvalBadge.className}>
                            {approvalBadge.label}
                          </span>
                        </td>
                        <td>{formatDate(stylist.createdAt)}</td>
                        <td>
                          {canMutateApproval && stylist.approvalStatus === 'pending' && (
                            <button
                              onClick={() => openConfirmModal('approve', stylist._id, stylist.name)}
                              className="btn-approve"
                              disabled={isActioning}
                            >
                              <i className="fas fa-check"></i> Approve
                            </button>
                          )}
                          {canMutateApproval && stylist.approvalStatus === 'approved' && (
                            <button
                              onClick={() => openConfirmModal('revoke', stylist._id, stylist.name)}
                              className="btn-revoke"
                              disabled={isActioning}
                            >
                              <i className="fas fa-ban"></i> Revoke
                            </button>
                          )}
                          {stylist.approvalStatus === 'rejected' && (
                            <span className="action-disabled">Rejected</span>
                          )}
                          {!canMutateApproval && stylist.approvalStatus !== 'rejected' && (
                            <span className="action-disabled">View only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal?.open && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirmModal.action === 'approve' ? 'Approve Stylist' : 'Revoke Approval'}</h2>
              <button
                onClick={closeConfirmModal}
                className="modal-close"
                disabled={isActioning}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to{' '}
                <strong>{confirmModal.action === 'approve' ? 'approve' : 'revoke approval for'}</strong>{' '}
                <strong>{confirmModal.name}</strong>?
              </p>
              <p className="action-note">
                {confirmModal.action === 'approve'
                  ? 'This will make the stylist visible to clients on the platform.'
                  : 'This will hide the stylist from clients on the platform.'}
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={closeConfirmModal}
                className="btn-secondary"
                disabled={isActioning}
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                className={confirmModal.action === 'approve' ? 'btn-primary btn-primary--approve' : 'btn-primary btn-primary--revoke'}
                disabled={isActioning}
              >
                {isActioning ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>{' '}
                    {confirmModal.action === 'approve' ? 'Approving...' : 'Revoking...'}
                  </>
                ) : (
                  <>
                    <i className={confirmModal.action === 'approve' ? 'fas fa-check' : 'fas fa-ban'}></i>{' '}
                    {confirmModal.action === 'approve' ? 'Approve' : 'Revoke'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StylistApprovalPage;
